import {
  ArchiveIcon,
  CaretUpDownIcon,
  ClockClockwiseIcon,
  DotsThreeVerticalIcon,
} from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { useState } from 'react';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ItemStatusEnum } from '@/modules/common';
import {
  ChangeStatusDialog,
  GenericTable,
  TableItemStatus,
} from '@/modules/common/components';

import { StyleItem } from '../types';

interface StylesTableProps {
  data: StyleItem[];
  onClick: (item: StyleItem) => void;
  toggleStatus: (item: StyleItem) => void;
}

export function StylesTable({ data, toggleStatus, onClick }: StylesTableProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeStyle, setActiveStyle] = useState<StyleItem | null>(null);
  const isArchived = activeStyle?.status === ItemStatusEnum.Archived;

  const onArchiveRestoreClick = (
    event: React.SyntheticEvent<HTMLDivElement>,
    styleRow: StyleItem
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveStyle(styleRow);
  };

  const toggleModal = (nextOpenState: boolean) => {
    if (!nextOpenState) {
      setActiveStyle(null);
    }
  };

  const toggleStatusStyle = async () => {
    if (activeStyle?.id) {
      setIsUpdating(true);
      try {
        await toggleStatus(activeStyle as StyleItem);
        setActiveStyle(null);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const columns: ColumnDef<StyleItem>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Style Name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('name')}</span>
      ),
    },
    {
      size: 100,
      accessorKey: 'imagePublicUrl',
      header: 'Preview',
      cell: ({ row }) => (
        <img
          src={row.getValue('imagePublicUrl')}
          alt="Preview"
          className="w-12 h-[52px] rounded-lg"
        />
      ),
    },
    {
      accessorKey: 'positivePrompts',
      header: 'Positive Prompt',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('positivePrompts')}</span>
      ),
    },
    {
      accessorKey: 'negativePrompts',
      header: 'Negative Prompt',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('negativePrompts')}</span>
      ),
    },
    {
      accessorKey: 'status',
      size: 200,
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Status
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => <TableItemStatus status={row.getValue('status')} />,
    },
    {
      size: 130,
      accessorKey: 'updatedAt',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Upload Date
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) =>
        format(new Date(row.getValue('updatedAt')), 'yyyy-MM-dd'),
    },
    {
      id: 'actions',
      size: 40,
      enableHiding: false,
      cell: ({ row }) => {
        const isArchived = row.original.status === ItemStatusEnum.Archived;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                data-testid="styles-table-actions"
              >
                <span className="sr-only">Open menu</span>
                <DotsThreeVerticalIcon color={colors.zinc[400]} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid={isArchived ? 'restore' : 'archive'}
                className={cn(
                  !isArchived && 'focus:text-destructive text-destructive'
                )}
                onClick={(event) => onArchiveRestoreClick(event, row.original)}
              >
                {isArchived ? (
                  <ClockClockwiseIcon />
                ) : (
                  <ArchiveIcon color="hsl(var(--destructive))" />
                )}
                {isArchived ? 'Restore' : 'Archive'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <GenericTable
        data={data}
        onClick={onClick}
        columns={columns}
        searchable
        searchColumns={['name']}
        searchPlaceholder="Search by style name"
        emptyDescription="No styles uploaded yet"
      />
      <ChangeStatusDialog
        onOpenChange={toggleModal}
        isArchived={isArchived}
        isUpdating={isUpdating}
        onSubmit={toggleStatusStyle}
        isOpen={Boolean(activeStyle?.id)}
        title={`${isArchived ? 'Restore' : 'Archive'} style “${activeStyle?.name}”?`}
        restoreDescription="Are you sure you want to restore this style?"
        archiveDescription="Are you sure you want to archive this style? You can restore it later if needed."
      />
    </>
  );
}
